import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, Download, Edit, Trash2 } from 'lucide-react';
import { User } from '../lib/types';
import { supabase } from '../lib/supabase';

interface ArticleCardProps {
  article: {
    id: string;
    title: string;
    description: string;
    image_url: string;
    category: string;
    slug: string;
  };
  user: User | null;
}

const ArticleCard: React.FC<ArticleCardProps> = ({ article, user }) => {
  const navigate = useNavigate();

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;

    try {
      if (article.image_url) {
        const imagePath = article.image_url.split('/').pop();
        if (imagePath) {
          await supabase.storage
            .from('article-images')
            .remove([`articles/${imagePath}`]);
        }
      }

      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', article.id);

      if (error) throw error;

      window.location.reload();
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Erreur lors de la suppression de l\'article');
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/resources/edit/${article.slug}`);
  };

  return (
    <Link
      to={`/resources/${article.slug}`}
      className="group bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-md"
    >
      {article.image_url && (
        <div className="h-48 overflow-hidden">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-110"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors">
            {article.title}
          </h3>
          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
            {article.category || 'Article'}
          </span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2">
          {article.description}
        </p>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="text-sm text-gray-700 ml-1">4.8/5</span>
            <span className="text-xs text-gray-500 ml-2">(1245 téléchargements)</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={(e) => e.preventDefault()}
              className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm"
            >
              <Download className="w-4 h-4 mr-1" />
              <span>Télécharger</span>
            </button>
            {user?.role === 'admin' && (
              <>
                <button
                  onClick={handleEdit}
                  className="text-gray-600 hover:text-gray-800 flex items-center text-sm"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  <span>Modifier</span>
                </button>
                <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800 flex items-center text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span>Supprimer</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;